import { Box, useTheme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { Context, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { mainComponentEventEndpoints } from 'src/api/main-component-event'
import NoReports from 'src/assets/svg/no-reports.svg'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import InfiniteScrollTable from 'src/components/table/InfiniteScrollTable'
import { IMainComponentEvent, MainComponentEventCategory } from 'src/generated-types/main-component-event'
import { MUIThemeColors } from 'src/mui-theme'
import { mainComponentEventQueryKeys } from 'src/query-keys'
import { getFormattedDate, getThemeColor } from 'src/utils'
import { getUserDetail, UserDetail } from 'src/utils/getUserDetails'

interface IMainComponentEventsProps {
  mainComponentId: EntityId
  context?: Context<any> | null
  withoutLayout?: boolean
  height?: string
  autoHeight?: boolean
  maxVisibleRowCount?: number
  minimalEmptyStatus?: boolean
  setDataLength?: (length: number) => void
}

const MainComponentEvents = ({
  mainComponentId,
  withoutLayout,
  height,
  autoHeight,
  maxVisibleRowCount,
  minimalEmptyStatus = false,
  setDataLength,
}: IMainComponentEventsProps) => {
  const { t } = useTranslation(['equipment', 'common', 'activity-logs'])
  const theme = useTheme()

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'category',
        headerName: t('event-category', { ns: 'equipment' }),
        flex: 1,
        renderCell: params => t(MainComponentEventCategory[params.value], { ns: 'equipment' }),
      },
      {
        field: 'title',
        headerName: t('title', { ns: 'common' }),
        flex: 1.5,
        renderCell: params => (
          <DynamicTextTypography text={params.value as string} fontWeight={500} maxLength={40} />
        ),
      },
      {
        field: 'createdBy',
        headerName: t('createdBy', { ns: 'common' }),
        flex: 1.2,
        renderCell: params => (
          <DynamicTextTypography
            text={getUserDetail(params.row.createdByUser, UserDetail.FullName)}
            fontWeight={500}
            maxLength={25}
          />
        ),
      },
      {
        field: 'createdAt',
        headerName: t('createdAt', { ns: 'common' }),
        flex: 1,
        renderCell: params =>
          params.value && !params.value?.startsWith('0001-01-01')
            ? getFormattedDate(new Date(params.value))
            : '-',
      },
      {
        field: 'effectiveDate',
        headerName: t('effective-date', { ns: 'activity-logs' }),
        flex: 1,
        renderCell: params =>
          params.value && !params.value?.startsWith('0001-01-01')
            ? getFormattedDate(new Date(params.value))
            : '-',
      },
    ],
    [t],
  )

  const eventsTable = (
    <InfiniteScrollTable<IMainComponentEvent>
      id="main-component-events-table"
      queryUrl={mainComponentEventEndpoints.getMainComponentEvents(mainComponentId)}
      generateQueryKey={(page: number, pageSize: number) =>
        mainComponentEventQueryKeys.GET_MAIN_COMPONENT_EVENTS(mainComponentId as EntityId, { page, pageSize })
      }
      pageSize={10}
      columns={columns}
      height={height}
      autoHeight={autoHeight}
      maxVisibleRowCount={maxVisibleRowCount}
      getRowHeight={() => 36}
      minRowHeight="36px"
      containerProps={{ sx: { overflowX: 'hidden' } }}
      noDataComponent={() => (
        <Box height="100%">
          <EmptyStatusCard
            imageWidth="90px"
            image={NoReports}
            header={t('equipment-events-empty-status-title', { ns: 'equipment' })}
            content={t('equipment-events-empty-status-content', { ns: 'equipment' })}
            imageContainerClasses="pb-4 pt-2"
            contentTopMargin="10px"
            headerFontSize="18px"
            contentFontSize="14px"
            contentClasses="mt-2"
            height="100%"
            hideImage={minimalEmptyStatus}
            compact={minimalEmptyStatus}
          />
        </Box>
      )}
      hideDefaultFooterPagination
      spinnerHeight={100}
      setDataLength={setDataLength}
    />
  )

  if (withoutLayout) {
    return eventsTable
  }

  return (
    <CommonPageLayout
      titleSection={{ subtitle: t('equipment-events', { ns: 'equipment' }), subtitleFontSize: '18px' }}
      topAction={[]}
      titleContainerProps={{ paddingBottom: '5px' }}
    >
      <Box padding="8px" bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius="4px">
        {eventsTable}
      </Box>
    </CommonPageLayout>
  )
}

export default MainComponentEvents
